import React, { useState, useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

// Components
import EstudianteForm from './components/Estudiante/EstudianteForm';
import EstudianteList from './components/Estudiante/EstudianteList';
import FileUpload from './components/Estudiante/FileUpload';
import FooterComponent from './components/FooterComponent';
import ForgotPassword from './components/ForgotPassword';
import Home from './components/Home';
import Login from './components/Login';
import PrivateRoute from './components/PrivateRoute';
import Profile from './components/Profile';
import Register from './components/Register';
import Rendicion from "./components/Rendicion/Rendicion";
import Rendicion2021 from "./components/Rendicion/Rendicion2021";
import TabletEntrega from './components/Tablet/TabletEntrega';
import TabletIngreso from "./components/Tablet/TabletIngreso";
import AuthService from './services/AuthService';
import ResetPassword from "./components/ResetPassword";
import PeriodosList from "./components/Admin/PeriodosList";
import PeriodoService from "./services/PeriodoService";
import UserList from "./components/User/UserList";
import Sidebar from './components/common/Sidebar';
import Navbar from './components/common/Navbar';

const App = () => {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [periodoActivo, setPeriodoActivo] = useState(null);

  useEffect(() => {
    const loadUser = async () => {
      const user = AuthService.getCurrentUser();
      if (user) {
        setCurrentUser(user);
        setIsAdmin(user.roles.includes("ROLE_ADMIN"));
        await cargarPeriodoActivo();
      }
    };
    
    loadUser();
  }, []);

  const cargarPeriodoActivo = async () => {
    try {
      const response = await PeriodoService.getActivo();
      if (response.data) {
        setPeriodoActivo(response.data);
      }
    } catch (error) {
      console.error('Error al cargar el período activo:', error);
    }
  };

  const logOut = () => {
    AuthService.logout();
    setCurrentUser(null);
    setIsAdmin(false);
    setPeriodoActivo(null);
    
    // Usar window.location para forzar una recarga completa en la ruta base
    if (process.env.NODE_ENV === 'production') {
      // En producción, redirigir a la ruta base con hash
      window.location.href = `/`;
    } else {
      // En desarrollo, usar la ruta relativa
      window.location.href = '/';
    }
  };

  // No mostrar sidebar en estas rutas
  const hideSidebar = [
    '/rendicion-de-cuentas', 
    '/rendicion-de-cuentas-2021', 
    '/', 
    '/home', 
    '/login', 
    '/register', 
    '/forgot-password', 
    '/reset-password'
  ].includes(location.pathname) || location.pathname.startsWith('/reset-password/');
  
  // Mostrar sidebar solo si el usuario está autenticado y no está en las rutas excluidas
  const shouldShowSidebar = currentUser && !hideSidebar;
  
  // Hide Navbar on specific routes and when user is logged in (except for login/register)
  const hideNavbar = [
    '/rendicion-de-cuentas', 
    '/rendicion-de-cuentas-2021'
  ].includes(location.pathname) || 
  (currentUser && !['/', '/home', '/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname) && 
   !location.pathname.startsWith('/reset-password/'));

  // Determinar si estamos en la ruta de estudiantes para aplicar estilos personalizados
  const isEstudiantesRoute = location.pathname.startsWith('/estudiantes');
  
  return (
    <div className="app-container d-flex flex-column min-vh-100">
      {!hideNavbar && (
        <Navbar currentUser={currentUser} isAdmin={isAdmin} logOut={logOut} />
      )}
      
      <div className="d-flex flex-grow-1">
        {shouldShowSidebar && (
          <Sidebar currentUser={currentUser} isAdmin={isAdmin} logOut={logOut} />
        )}
        
        <div 
          className="main-content flex-grow-1 d-flex flex-column" 
          style={{ 
            marginLeft: shouldShowSidebar ? '250px' : '0',
            width: shouldShowSidebar ? 'calc(100% - 250px)' : '100%',
            transition: 'all 0.3s ease',
            paddingTop: (location.pathname === '/' || location.pathname === '/home') ? '60px' : '0'
          }} 
          id="main-content"
        >
          <main className="flex-grow-1 py-4">
            <Container fluid className="px-4" style={{ maxWidth: '100%' }}>
              {periodoActivo && location.pathname !== '/' && location.pathname !== '/home' && (
                <div className="alert alert-info mb-4" role="alert">
                  <i className="fas fa-info-circle me-2 pr-2"></i>
                  Período académico activo: <strong>{periodoActivo.descripcion}</strong>
                </div>
              )}
              
              <Switch>
                <Route exact path={["/", "/home"]} component={Home} />
                <Route exact path="/login" component={Login} />
                <Route exact path="/register" component={Register} />
                <Route exact path="/forgot-password" component={ForgotPassword} />
                <Route exact path="/reset-password/:token" component={ResetPassword} />
                
                {/* Rutas privadas */}
                <PrivateRoute path="/profile" component={Profile} currentUser={currentUser} />
                <PrivateRoute path="/estudiantes" component={EstudianteList} currentUser={currentUser} />
                <PrivateRoute path="/estudiante/:id" component={EstudianteForm} currentUser={currentUser} />
                <PrivateRoute path="/estudiante" component={EstudianteForm} currentUser={currentUser} />
                <PrivateRoute path="/inscripcion" component={EstudianteForm} currentUser={currentUser} />
                <PrivateRoute path="/cargar-estudiantes" component={FileUpload} currentUser={currentUser} />
                <PrivateRoute path="/tablet/ingreso" component={TabletIngreso} currentUser={currentUser} />
                <PrivateRoute path="/tablet/entrega" component={TabletEntrega} currentUser={currentUser} />
                <PrivateRoute path="/rendicion" component={Rendicion} currentUser={currentUser} />
                
                {/* Rutas de administrador */}
                {isAdmin && (
                  <>
                    <PrivateRoute path="/admin/usuarios" component={UserList} currentUser={currentUser} roles={["ROLE_ADMIN"]} />
                    <PrivateRoute path="/admin/periodos" component={PeriodosList} currentUser={currentUser} roles={["ROLE_ADMIN"]} />
                    <PrivateRoute path="/reporte" component={EstudianteList} currentUser={currentUser} roles={["ROLE_ADMIN"]} />
                    <PrivateRoute path="/acta" component={TabletEntrega} currentUser={currentUser} roles={["ROLE_ADMIN"]} />
                    <PrivateRoute path="/tablet-ingreso" component={TabletIngreso} currentUser={currentUser} roles={["ROLE_ADMIN"]} />
                  </>
                )}
                
                {/* Rutas públicas especiales */}
                <Route path="/rendicion-de-cuentas" component={Rendicion} />
                <Route path="/rendicion-de-cuentas-2021" component={Rendicion2021} />
              </Switch>
            </Container>
          </main>
          
          {!hideSidebar && currentUser && <FooterComponent />}
        </div>
      </div>
    </div>
  );
}

export default App;
